// M09 - EducaFileUploadWizard
//
// Modal con AntD Steps para subir un fichero al bucket EducaFiles. La
// subida real es PUT directo a GCS via signed URL (XHR con progress).
// El cliente nunca pasa el blob por el server: el server solo emite la
// URL y luego confirma metadata real.
//
// Pasos:
//   1. Destino: carpeta + visibilidad + nombre opcional + descripcion
//   2. Fichero: drag-drop o boton. Valida mime/ext/size local.
//   3. Subida: progress bar + boton copiar URL al terminar.

import React, { useState, useMemo, useCallback } from "react";
import {
  Modal,
  Steps,
  Form,
  Select,
  Input,
  Radio,
  Upload,
  Button,
  Progress,
  Alert,
  Result,
  Space,
  message,
} from "antd";
import {
  InboxOutlined,
  GlobalOutlined,
  LockOutlined,
  CopyOutlined,
  CloudUploadOutlined,
} from "@ant-design/icons";
import { ACCEPT_ATTR, validateFile } from "./educaFilesAllowed";

const { Dragger } = Upload;

export function EducaFileUploadWizard({
  open,
  folders,
  defaultFolder,
  accessToken,
  api,
  onClose,
  onUploaded,
}) {
  const [step, setStep] = useState(0);
  const [form] = Form.useForm();
  const [file, setFile] = useState(null);
  const [validation, setValidation] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null); // { file, streamUrl }
  const [error, setError] = useState(null);

  const folderOptions = useMemo(
    () => [
      { value: "", label: "EducaFiles (raiz)" },
      ...folders.map((f) => ({ value: f.name, label: f.name })),
    ],
    [folders],
  );

  const reset = useCallback(() => {
    setStep(0);
    setFile(null);
    setValidation(null);
    setProgress(0);
    setUploading(false);
    setResult(null);
    setError(null);
    form.resetFields();
  }, [form]);

  const close = useCallback(() => {
    if (uploading) {
      message.warning("Espera a que termine la subida o cancela");
      return;
    }
    onClose();
    setTimeout(reset, 250);
  }, [uploading, onClose, reset]);

  const handlePickFile = useCallback((picked) => {
    const v = validateFile(picked);
    setFile(picked);
    setValidation(v);
    return false; // evita que Upload haga el POST automatico
  }, []);

  const startUpload = useCallback(async () => {
    setError(null);
    setUploading(true);
    setProgress(0);
    try {
      const values = form.getFieldsValue();
      const payload = {
        originalFilename: file.name,
        contentType: validation.mime || file.type || "application/octet-stream",
        size: file.size,
        folder: values.folder || "",
        name: values.name || file.name,
        description: values.description || "",
        visibility: values.visibility || "private",
      };
      const created = await api.createUploadUrl(accessToken, payload);
      await api.uploadToSignedUrl({
        url: created.upload.url,
        file,
        contentType: created.upload.contentType,
        onProgress: setProgress,
      });
      const confirmed = await api.confirmUpload(accessToken, created.file.id);
      setResult({
        file: confirmed.file,
        streamUrl: api.streamUrl(confirmed.file.id),
      });
      onUploaded?.(confirmed.file);
    } catch (e) {
      setError(e?.body?.message || e?.message || "Error subiendo");
    } finally {
      setUploading(false);
    }
  }, [file, validation, form, accessToken, api, onUploaded]);

  const canGoFromStep0 = true; // todos los campos son opcionales o tienen default
  const canGoFromStep1 = !!file && validation && validation.ok;

  const nextLabel = step === 1 ? "Subir" : "Siguiente";
  const next = () => {
    if (step === 0 && !canGoFromStep0) return;
    if (step === 1 && !canGoFromStep1) return;
    if (step === 1) {
      setStep(2);
      // arranca automaticamente la subida al entrar en el paso 3
      setTimeout(startUpload, 50);
      return;
    }
    setStep((s) => s + 1);
  };
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const initialFolder =
    defaultFolder && defaultFolder !== "__root__" ? defaultFolder : "";

  return (
    <Modal
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <CloudUploadOutlined /> Subir fichero a EducaFiles
        </span>
      }
      open={open}
      onCancel={close}
      width={680}
      destroyOnClose
      footer={
        <Space>
          {step < 2 ? (
            <Button onClick={close} disabled={uploading}>
              Cancelar
            </Button>
          ) : null}
          {step > 0 && step < 2 ? <Button onClick={prev}>Atras</Button> : null}
          {step < 2 ? (
            <Button
              type="primary"
              onClick={next}
              disabled={step === 1 && !canGoFromStep1}
            >
              {nextLabel}
            </Button>
          ) : (
            <Button type="primary" onClick={close} disabled={uploading}>
              {result ? "Cerrar" : "Cerrar y cancelar"}
            </Button>
          )}
        </Space>
      }
    >
      <Steps
        current={step}
        size="small"
        items={[
          { title: "Destino" },
          { title: "Fichero" },
          { title: "Subida" },
        ]}
        style={{ marginBottom: 24 }}
      />

      {step === 0 && (
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={{
            folder: initialFolder,
            visibility: "private",
          }}
        >
          <Form.Item name="folder" label="Carpeta destino">
            <Select options={folderOptions} />
          </Form.Item>
          <Form.Item
            name="visibility"
            label="Visibilidad"
            rules={[{ required: true }]}
          >
            <Radio.Group>
              <Radio.Button value="private">
                <LockOutlined /> Privada (requiere admin)
              </Radio.Button>
              <Radio.Button value="public">
                <GlobalOutlined /> Publica (URL externa)
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="name"
            label="Nombre visible (opcional)"
            extra="Si lo dejas vacio se usa el nombre original del fichero"
          >
            <Input maxLength={120} placeholder="Ej. Logo principal 2026" />
          </Form.Item>
          <Form.Item name="description" label="Descripcion (opcional)">
            <Input.TextArea
              rows={2}
              maxLength={500}
              showCount
              placeholder="Para que se va a usar"
            />
          </Form.Item>
        </Form>
      )}

      {step === 1 && (
        <div>
          <Dragger
            multiple={false}
            showUploadList={false}
            beforeUpload={handlePickFile}
            accept={ACCEPT_ATTR}
            style={{ padding: 12 }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Arrastra el fichero aqui o haz clic para elegir
            </p>
            <p className="ant-upload-hint" style={{ fontSize: 12 }}>
              Imagenes, videos, audio, PDFs, Office o zips. Maximo 8 GB.
            </p>
          </Dragger>

          {file ? (
            <div
              style={{
                marginTop: 16,
                padding: 12,
                borderRadius: 10,
                background: "#f8f9fb",
                border: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <div style={{ fontWeight: 600, color: "#2b323f" }}>
                {file.name}
              </div>
              <div style={{ fontSize: 12, color: "rgba(0,0,0,0.55)" }}>
                {(file.size / (1024 * 1024)).toFixed(2)} MB
                {validation?.ok ? ` · ${validation.kind}` : null}
              </div>
              {!validation?.ok ? (
                <Alert
                  type="error"
                  message={validation?.reason || "Fichero invalido"}
                  showIcon
                  style={{ marginTop: 8 }}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      )}

      {step === 2 && (
        <div>
          {!result && !error ? (
            <div style={{ padding: 20 }}>
              <p style={{ marginTop: 0 }}>Subiendo a Google Cloud Storage...</p>
              <Progress
                percent={progress}
                status={uploading ? "active" : "normal"}
                strokeColor={{ from: "#ed773c", to: "#245e55" }}
              />
              <p style={{ fontSize: 12, color: "rgba(0,0,0,0.5)" }}>
                Si el fichero es grande puede tardar varios minutos. No cierres
                la pestana.
              </p>
            </div>
          ) : null}
          {error ? (
            <Alert
              type="error"
              showIcon
              message="No se pudo completar la subida"
              description={error}
            />
          ) : null}
          {result ? (
            <Result
              status="success"
              title="Fichero subido"
              subTitle={`${result.file.name} esta listo en EducaFiles${result.file.folder ? ` / ${result.file.folder}` : ""}.`}
              extra={[
                <Button
                  key="copy"
                  type="primary"
                  icon={<CopyOutlined />}
                  onClick={() => {
                    try {
                      navigator.clipboard.writeText(result.streamUrl);
                      message.success("URL copiada");
                    } catch (e) {
                      message.error("No se pudo copiar");
                    }
                  }}
                >
                  Copiar URL
                </Button>,
                <Button key="close" onClick={close}>
                  Cerrar
                </Button>,
              ]}
            />
          ) : null}
        </div>
      )}
    </Modal>
  );
}
